import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '@/components/feature/Footer';

// ─── Counter animation hook ───────────────────────────────────────────────────
const useCounter = (target: number, duration = 1800, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

// ─── Data ─────────────────────────────────────────────────────────────────────
const values = [
  {
    icon: 'ri-heart-3-line',
    title: 'الإخلاص للمجتمع',
    desc: 'نعمل بدافع خدمة أبناء المدينة المنورة والمملكة، لا بدافع الربح. كل قرار نتخذه يصبّ في مصلحة المجتمع.',
    color: 'bg-rose-50 text-rose-500',
  },
  {
    icon: 'ri-eye-2-line',
    title: 'الشفافية التامة',
    desc: 'كل ريال يُجمع ويُصرف موثّق ومتاح للجميع. لا أسرار، لا غموض — فقط ثقة مبنية على الوضوح.',
    color: 'bg-sky-50 text-sky-500',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'الأمانة والمصداقية',
    desc: 'نتحقق من هوية كل مبدع وكل حملة قبل النشر. ثقتك أمانة نحملها بجدية تامة.',
    color: 'bg-emerald-50 text-emerald-600',
  },
  {
    icon: 'ri-community-line',
    title: 'بناء المجتمع',
    desc: 'هِبة ليست مجرد منصة — هي مجتمع يؤمن بأن التعاون والتكافل يصنعان فرقاً حقيقياً.',
    color: 'bg-amber-50 text-amber-500',
  },
];

const team = [
  {
    name: 'عبدالله المدني',
    role: 'المؤسس والمدير التنفيذي',
    bio: 'ابن المدينة المنورة، رائد أعمال اجتماعي بخبرة 10 سنوات في التقنية والعمل الخيري.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Saudi%20man%20portrait%20clean%20white%20background%20warm%20confident%20smile%20beard%20formal%20attire&width=300&height=300&seq=901&orientation=squarish',
  },
  {
    name: 'منى الأنصاري',
    role: 'مديرة العمليات',
    bio: 'متخصصة في إدارة المنظمات غير الربحية، تؤمن بأن التنظيم الجيد هو أساس الأثر الحقيقي.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Saudi%20woman%20hijab%20portrait%20clean%20white%20background%20warm%20confident%20smile%20elegant&width=300&height=300&seq=902&orientation=squarish',
  },
  {
    name: 'فيصل الحربي',
    role: 'مدير التقنية',
    bio: 'مهندس برمجيات شغوف ببناء منتجات تخدم المجتمع، يؤمن بأن التقنية أداة للخير.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Saudi%20young%20man%20portrait%20clean%20white%20background%20warm%20confident%20smile%20casual%20tech&width=300&height=300&seq=903&orientation=squarish',
  },
  {
    name: 'ريم العمري',
    role: 'مديرة المجتمع والشراكات',
    bio: 'ناشطة اجتماعية من المدينة المنورة، تبني جسور التواصل بين المبدعين والداعمين.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Saudi%20woman%20hijab%20portrait%20clean%20white%20background%20warm%20confident%20smile%20community%20leader&width=300&height=300&seq=904&orientation=squarish',
  },
];

const milestones = [
  { year: '2022', title: 'بداية الفكرة', desc: 'وُلدت فكرة هِبة في المدينة المنورة من رؤية بسيطة: كيف نربط المبدعين المحليين بمن يؤمن بهم؟' },
  { year: '2023', title: 'الإطلاق التجريبي', desc: 'أطلقنا النسخة التجريبية بـ 20 حملة منتقاة، وجمعنا أول 500,000 ريال لمشاريع محلية.' },
  { year: '2024', title: 'النمو والتوسع', desc: 'تجاوزنا 500 حملة ناجحة وانضم أكثر من 15,000 داعم من مختلف مناطق المملكة.' },
  { year: '2025', title: 'الترخيص الرسمي', desc: 'حصلنا على الترخيص الرسمي كمنصة غير ربحية معتمدة في المملكة العربية السعودية.' },
  { year: '2026', title: 'اليوم', desc: 'أكثر من 2,000 حملة ناجحة، 50,000 داعم، و30 مليون ريال جُمعت لأحلام حقيقية.' },
];

// ─── Stats section with animation ────────────────────────────────────────────
const StatsSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const campaigns  = useCounter(2000,  1800, visible);
  const donors     = useCounter(50000, 2000, visible);
  const raised     = useCounter(30,    1600, visible);
  const successRate = useCounter(89,   1400, visible);

  return (
    <div ref={ref} className="grid grid-cols-2 lg:grid-cols-4 gap-5">
      {[
        { value: `+${campaigns.toLocaleString('en-US')}`, label: 'حملة ناجحة', icon: 'ri-flag-line', color: 'text-emerald-600' },
        { value: `+${donors.toLocaleString('en-US')}`,   label: 'داعم كريم',   icon: 'ri-user-heart-line', color: 'text-rose-500' },
        { value: `+${raised}M`,                          label: 'ريال جُمع',   icon: 'ri-money-dollar-circle-line', color: 'text-amber-500' },
        { value: `${successRate}%`,                      label: 'نسبة النجاح', icon: 'ri-bar-chart-line', color: 'text-sky-500' },
      ].map((stat, i) => (
        <div key={i} className="bg-white rounded-2xl border border-stone-100 p-6 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-stone-50 mx-auto mb-3">
            <i className={`${stat.icon} text-xl ${stat.color}`}></i>
          </div>
          <div className={`text-3xl font-black mb-1 ${stat.color}`}>{stat.value}</div>
          <div className="text-stone-500 text-sm">{stat.label}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const AboutPage = () => (
  <div className="min-h-screen bg-stone-50" dir="rtl">

    {/* ── Hero ── */}
    <section className="relative min-h-[70vh] flex items-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Madinah%20Al%20Munawwarah%20aerial%20view%20golden%20sunset%20Prophet%20mosque%20minarets%20warm%20light%20serene%20spiritual%20Saudi%20Arabia%20beautiful%20cityscape&width=1600&height=900&seq=801&orientation=landscape"
        alt="المدينة المنورة"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-l from-black/75 via-black/55 to-black/30"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-8 md:px-12 pt-24 pb-16">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1.5 mb-5">
            <i className="ri-map-pin-2-line text-emerald-400 text-sm"></i>
            <span className="text-white/90 text-sm">من قلب المدينة المنورة</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-5">
            قصتنا مع
            <br />
            <span className="text-emerald-400">هِبة</span>
          </h1>
          <p className="text-white/80 text-lg leading-relaxed max-w-xl">
            وُلدنا في المدينة المنورة من إيمان راسخ بأن كل إنسان يستحق فرصة لتحقيق حلمه، وأن المجتمع حين يتكاتف يصنع المعجزات.
          </p>
        </div>
      </div>
    </section>

    {/* ── Mission ── */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-emerald-600 text-sm font-semibold tracking-wide uppercase">رسالتنا</span>
            <h2 className="text-3xl md:text-4xl font-black text-stone-800 mt-2 mb-5 leading-tight">
              نبني جسراً بين
              <br />
              الحلم والواقع
            </h2>
            <p className="text-stone-600 leading-relaxed mb-5">
              هِبة منصة تمويل جماعي غير ربحية أُسِّست في المدينة المنورة لخدمة أبناء المملكة العربية السعودية. نؤمن بأن كل مبدع وكل صاحب قضية يستحق أن يُسمع ويُدعم.
            </p>
            <p className="text-stone-600 leading-relaxed mb-6">
              لسنا شركة تسعى للربح — نحن مجتمع يؤمن بالتكافل والعطاء. الرسوم الإدارية البسيطة التي نجمعها تذهب حصراً لتغطية تكاليف التشغيل وضمان استمرار الخدمة للجميع.
            </p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 bg-emerald-50 rounded-full px-4 py-2">
                <i className="ri-checkbox-circle-fill text-emerald-600 text-sm"></i>
                <span className="text-emerald-800 text-sm font-semibold">منصة غير ربحية</span>
              </div>
              <div className="flex items-center gap-2 bg-emerald-50 rounded-full px-4 py-2">
                <i className="ri-checkbox-circle-fill text-emerald-600 text-sm"></i>
                <span className="text-emerald-800 text-sm font-semibold">مرخصة رسمياً</span>
              </div>
              <div className="flex items-center gap-2 bg-emerald-50 rounded-full px-4 py-2">
                <i className="ri-checkbox-circle-fill text-emerald-600 text-sm"></i>
                <span className="text-emerald-800 text-sm font-semibold">شفافية كاملة</span>
              </div>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=Saudi%20Arabia%20community%20people%20gathering%20helping%20each%20other%20warm%20golden%20light%20Madinah%20city%20streets%20authentic%20local%20culture%20unity&width=700&height=500&seq=802&orientation=landscape"
              alt="مجتمع هِبة"
              className="w-full h-80 object-cover object-top rounded-3xl"
            />
            <div className="absolute -bottom-5 -right-5 bg-emerald-600 text-white rounded-2xl p-5 hidden md:block">
              <div className="text-3xl font-black">4+</div>
              <div className="text-emerald-200 text-sm">سنوات من العطاء</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Stats ── */}
    <section className="py-16 bg-stone-50">
      <div className="max-w-7xl mx-auto px-8 md:px-12">
        <div className="text-center mb-10">
          <span className="text-emerald-600 text-sm font-semibold tracking-wide uppercase">أثرنا بالأرقام</span>
          <h2 className="text-2xl md:text-3xl font-black text-stone-800 mt-2">ما حققناه معاً</h2>
        </div>
        <StatsSection />
      </div>
    </section>

    {/* ── Story Timeline ── */}
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-8 md:px-12">
        <div className="text-center mb-14">
          <span className="text-emerald-600 text-sm font-semibold tracking-wide uppercase">رحلتنا</span>
          <h2 className="text-2xl md:text-4xl font-black text-stone-800 mt-2">من الفكرة إلى الأثر</h2>
        </div>

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute right-6 top-0 bottom-0 w-px bg-stone-200 hidden md:block"></div>

          <div className="space-y-8">
            {milestones.map((m, i) => (
              <div key={i} className="flex gap-6 items-start">
                {/* Year bubble */}
                <div className="relative shrink-0 hidden md:flex flex-col items-center">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full border-2 font-black text-xs z-10 ${
                    i === milestones.length - 1
                      ? 'bg-emerald-600 border-emerald-600 text-white'
                      : 'bg-white border-stone-200 text-stone-600'
                  }`}>
                    {m.year}
                  </div>
                </div>

                {/* Content */}
                <div className={`flex-1 rounded-2xl p-5 border transition-all ${
                  i === milestones.length - 1
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-stone-50 border-stone-100'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full md:hidden ${
                      i === milestones.length - 1 ? 'bg-emerald-600 text-white' : 'bg-stone-200 text-stone-600'
                    }`}>{m.year}</span>
                    <h3 className={`font-black text-base ${i === milestones.length - 1 ? 'text-emerald-800' : 'text-stone-800'}`}>
                      {m.title}
                    </h3>
                  </div>
                  <p className={`text-sm leading-relaxed ${i === milestones.length - 1 ? 'text-emerald-700' : 'text-stone-500'}`}>
                    {m.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ── Values ── */}
    <section className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-8 md:px-12">
        <div className="text-center mb-14">
          <span className="text-emerald-600 text-sm font-semibold tracking-wide uppercase">ما نؤمن به</span>
          <h2 className="text-2xl md:text-4xl font-black text-stone-800 mt-2">قيمنا الأساسية</h2>
          <p className="text-stone-500 mt-3 text-sm max-w-md mx-auto">
            هذه القيم ليست شعارات — هي المبادئ التي تحكم كل قرار نتخذه
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v, i) => (
            <div key={i} className="bg-white rounded-2xl border border-stone-100 p-6 hover:border-emerald-200 transition-all">
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${v.color}`}>
                <i className={`${v.icon} text-xl`}></i>
              </div>
              <h3 className="font-bold text-stone-800 mb-2 text-sm">{v.title}</h3>
              <p className="text-stone-500 text-xs leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Team ── */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-8 md:px-12">
        <div className="text-center mb-14">
          <span className="text-emerald-600 text-sm font-semibold tracking-wide uppercase">الفريق</span>
          <h2 className="text-2xl md:text-4xl font-black text-stone-800 mt-2">الناس خلف هِبة</h2>
          <p className="text-stone-500 mt-3 text-sm max-w-md mx-auto">
            فريق صغير بقلوب كبيرة، يعمل بشغف لخدمة مجتمعه
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((member, i) => (
            <div key={i} className="text-center group">
              <div className="relative w-28 h-28 mx-auto mb-4">
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="w-28 h-28 rounded-2xl object-cover object-top border-2 border-stone-100 group-hover:border-emerald-300 transition-all"
                />
                <div className="absolute -bottom-2 -left-2 w-8 h-8 flex items-center justify-center bg-emerald-600 rounded-xl">
                  <i className="ri-verified-badge-fill text-white text-sm"></i>
                </div>
              </div>
              <h3 className="font-bold text-stone-800 mb-0.5">{member.name}</h3>
              <p className="text-emerald-600 text-xs font-semibold mb-2">{member.role}</p>
              <p className="text-stone-500 text-xs leading-relaxed">{member.bio}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Madinah connection ── */}
    <section className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-8 md:px-12">
        <div className="relative overflow-hidden rounded-3xl">
          <img
            src="https://readdy.ai/api/search-image?query=Madinah%20Al%20Munawwarah%20city%20streets%20local%20people%20community%20market%20warm%20golden%20hour%20authentic%20Saudi%20Arabia%20vibrant%20life&width=1400&height=500&seq=803&orientation=landscape"
            alt="المدينة المنورة"
            className="w-full h-72 md:h-96 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          <div className="absolute bottom-0 right-0 left-0 p-8 md:p-12">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-map-pin-fill text-emerald-400 text-lg"></i>
                <span className="text-emerald-400 text-sm font-semibold">المدينة المنورة — موطننا</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-white mb-3 leading-tight">
                من أرض البركة
                <br />
                نبني المستقبل
              </h2>
              <p className="text-white/80 text-sm md:text-base leading-relaxed">
                اخترنا المدينة المنورة مقراً لنا لأنها تجسّد معنى العطاء والتكافل. من هذه الأرض المباركة، نسعى لنشر ثقافة الدعم والإبداع في كل أرجاء المملكة.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── CTA ── */}
    <section className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-8 md:px-12 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-2xl mx-auto mb-6">
          <i className="ri-hand-heart-line text-emerald-600 text-3xl"></i>
        </div>
        <h2 className="text-2xl md:text-4xl font-black text-stone-800 mb-4">
          كن جزءاً من القصة
        </h2>
        <p className="text-stone-500 leading-relaxed mb-8 max-w-xl mx-auto">
          سواء كنت مبدعاً يحمل فكرة، أو داعماً يريد أن يصنع فرقاً — هِبة هي مكانك.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/create"
            className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-circle-line"></i>
            أطلق حملتك
          </Link>
          <Link
            to="/explore"
            className="inline-flex items-center justify-center gap-2 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-compass-3-line"></i>
            استكشف الحملات
          </Link>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default AboutPage;
